import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Robot, PaperPlaneTilt, X, CircleNotch } from '@phosphor-icons/react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AssistantBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Bonjour ! Je suis l\'assistant SIMBLYO. Comment puis-je vous aider ?'
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (isOpen) {
      fetchSuggestions();
    }
  }, [isOpen, location.pathname]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchSuggestions = async () => {
    try {
      const page = location.pathname.replace('/', '') || 'dashboard';
      const response = await axios.get(`${BACKEND_URL}/api/assistant/suggestions?page=${page}`);
      setSuggestions(response.data.suggestions);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const sendMessage = async (text = input) => {
    if (!text.trim() || loading) return;

    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const page = location.pathname.replace('/', '') || 'dashboard';
      const response = await axios.post(
        `${BACKEND_URL}/api/assistant/chat`,
        { message: text, context: page },
        { withCredentials: true }
      );
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response
      }]);
    } catch (error) {
      const errorMessage = error.response?.status === 401
        ? 'Connectez-vous pour utiliser l\'assistant.'
        : 'Désolé, une erreur est survenue. Réessayez.';
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: errorMessage
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Bouton flottant - À GAUCHE sur mobile pour ne pas cacher le contenu */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-20 sm:bottom-6 left-4 sm:left-auto sm:right-6 w-14 h-14 rounded-full bg-gradient-to-r from-violet-500 to-pink-500 shadow-lg flex items-center justify-center hover:scale-110 transition-transform glow"
        style={{ zIndex: 99999 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        data-testid="assistant-toggle"
      >
        {isOpen ? (
          <X size={24} weight="bold" className="text-white" />
        ) : (
          <Robot size={28} weight="duotone" className="text-white" />
        )}
      </motion.button>

      {/* Fenêtre de chat - À GAUCHE sur mobile */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-36 sm:bottom-24 left-4 sm:left-auto sm:right-6 w-[calc(100%-2rem)] sm:w-96 h-[450px] sm:h-[500px] glass-card overflow-hidden flex flex-col shadow-2xl"
            style={{ zIndex: 99999 }}
            data-testid="assistant-window"
          >
            {/* Header */}
            <div className="p-4 border-b border-white/[0.08] bg-gradient-to-r from-violet-500/15 to-pink-500/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/25 to-pink-500/25 flex items-center justify-center">
                  <Robot size={22} weight="duotone" className="text-violet-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white/90 font-sans">Assistant SIMBLYO</h3>
                  <p className="text-xs text-white/40">Je suis là pour vous aider</p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] p-3 rounded-2xl text-sm ${
                      msg.role === 'user'
                        ? 'bg-primary text-white rounded-br-md'
                        : 'bg-white/10 rounded-bl-md'
                    }`}
                  >
                    {msg.content}
                  </div>
                </motion.div>
              ))}
              
              {loading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-white/10 p-3 rounded-2xl rounded-bl-md">
                    <CircleNotch size={20} className="animate-spin text-primary" />
                  </div>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Suggestions */}
            {suggestions.length > 0 && messages.length <= 2 && (
              <div className="px-4 pb-2">
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => sendMessage(suggestion)}
                      className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="p-4 border-t border-white/[0.08]">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Posez votre question..."
                  className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-violet-500/50 transition-colors"
                  data-testid="assistant-input"
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || loading}
                  className="p-2.5 rounded-xl bg-gradient-to-r from-violet-500 to-pink-500 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <PaperPlaneTilt size={18} weight="fill" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
